import "@glideapps/glide-data-grid/dist/index.css";

import {
  DataEditorCore as DataEditor,
  sprites,
} from "@glideapps/glide-data-grid";
import { cn } from "@volca/ui/lib/utils";
import type { AdaptedSpreadsheetSheet } from "./adapter";
import { isFormulaSourceRange } from "./formula-fill";
import { volcaColor } from "./spreadsheet-theme";
import type { SpreadsheetViewer } from "./use-spreadsheet-viewer";
import {
  CUSTOM_CELL_RENDERERS,
  SPREADSHEET_ROW_MARKER_WIDTH,
} from "./viewer-helpers";

interface SpreadsheetGridProps {
  viewer: SpreadsheetViewer;
  adaptedSheet: AdaptedSpreadsheetSheet;
  areCommentsEnabled: boolean;
  canAskAiAboutCell: boolean;
  canNoteFromSelection: boolean;
}

export const SpreadsheetGrid = ({
  viewer,
  adaptedSheet,
  areCommentsEnabled,
  canAskAiAboutCell,
  canNoteFromSelection,
}: SpreadsheetGridProps) => {
  const {
    gridRef,
    gridWrapperRef,
    columns,
    gridHighlightRegions,
    rowResize,
    sheetLayout,
    clipboard: { handleCopyCapture, handlePasteCapture },
    sheet: {
      canMutateActiveSheet,
      hasActiveRegionView,
      frozenColumn,
      frozenRow,
      cancelViewportRestore,
      handleGridScrollCapture,
    },
    ui: {
      gridSelection,
      selectionBeforePointerDownRef,
      lastContextPointerRef,
      handleGridSelectionChange,
    },
    renderers: { gridTheme, gridRenderers, gridImageWindowLoader },
    draw: { drawCell, drawGridHeader },
    keyboard: { handleGridKeyDown, handleFinishedEditing },
    pointer: {
      canViewEditedCells,
      handleCellClicked,
      handleCellContextMenu,
      handleHeaderContextMenu,
      handleCellActivated,
      hasCellLinks,
    },
    cellEdits: { commitCellEdit, handleCellsEdited },
    bulkEdits: { handlePaste, handleDelete },
    handleFormulaFillPattern,
    handleRowAppended,
  } = viewer;

  return (
    <div
      ref={gridWrapperRef}
      onPasteCapture={handlePasteCapture}
      onCopyCapture={handleCopyCapture}
      onScrollCapture={handleGridScrollCapture}
      onPointerDownCapture={(event) => {
        cancelViewportRestore();
        selectionBeforePointerDownRef.current = gridSelection;
        lastContextPointerRef.current = {
          x: event.clientX,
          y: event.clientY,
        };
        rowResize.onPointerDownCapture(event);
      }}
      onPointerMoveCapture={rowResize.onPointerMoveCapture}
      onPointerUpCapture={rowResize.onPointerUpCapture}
      onPointerCancelCapture={rowResize.onPointerCancelCapture}
      onLostPointerCapture={rowResize.onPointerCancelCapture}
      onPointerLeave={rowResize.onPointerLeave}
      onWheelCapture={cancelViewportRestore}
      onKeyDownCapture={cancelViewportRestore}
      onContextMenuCapture={(event) => {
        lastContextPointerRef.current = {
          x: event.clientX,
          y: event.clientY,
        };
      }}
      className={cn(
        "quiet-scrollbar h-full min-h-0 w-full min-w-0 spreadsheet-overscroll-guard",
        {
          "cursor-row-resize [&_.dvn-scroller]:cursor-row-resize! [&_canvas]:cursor-row-resize!":
            rowResize.isResizingRow,
        },
      )}
    >
      <DataEditor
        ref={gridRef}
        columns={columns}
        rows={adaptedSheet.rowCount}
        getCellContent={([col, row]) => adaptedSheet.getCellContent(col, row)}
        getCellsForSelection={adaptedSheet.getCellsForSelection}
        customRenderers={CUSTOM_CELL_RENDERERS}
        onColumnResize={
          sheetLayout.canResize ? sheetLayout.onColumnResize : undefined
        }
        onColumnResizeEnd={
          sheetLayout.canResize ? sheetLayout.onColumnResizeEnd : undefined
        }
        rowHeight={rowResize.rowHeight}
        onVisibleRegionChanged={rowResize.onVisibleRegionChanged}
        width="100%"
        height="100%"
        rowMarkers={{
          kind: "clickable-number",
          width: SPREADSHEET_ROW_MARKER_WIDTH,
          theme: { bgCell: volcaColor("--neutral-subtle") },
        }}
        theme={gridTheme}
        drawHeader={drawGridHeader}
        renderers={gridRenderers}
        headerIcons={sprites}
        imageWindowLoader={gridImageWindowLoader}
        freezeRows={Math.min(frozenRow ?? 0, adaptedSheet.rowCount)}
        freezeColumns={frozenColumn ?? 0}
        smoothScrollX
        smoothScrollY
        keybindings={{ scrollToSelectedCell: false }}
        rangeSelect="multi-rect"
        onHeaderContextMenu={
          canMutateActiveSheet ? handleHeaderContextMenu : undefined
        }
        columnSelect="single"
        rowSelect="single"
        highlightRegions={gridHighlightRegions}
        gridSelection={gridSelection}
        onGridSelectionChange={handleGridSelectionChange}
        onCellClicked={handleCellClicked}
        // A cell that names something opens on double-click or Enter, not
        // on a second click: clicking a selected cell again must not leave.
        {...(hasCellLinks
          ? {
              onCellActivated: handleCellActivated,
              cellActivationBehavior: "double-click" as const,
            }
          : {})}
        onKeyDown={handleGridKeyDown}
        onFinishedEditing={handleFinishedEditing}
        onCellEdited={commitCellEdit}
        onCellsEdited={handleCellsEdited}
        drawCell={drawCell}
        {...(canMutateActiveSheet
          ? {
              fillHandle: isFormulaSourceRange(
                gridSelection.current?.range,
                adaptedSheet.getCellRecord,
              ),
              allowedFillDirections: "orthogonal" as const,
              onFillPattern: handleFormulaFillPattern,
              onPaste: handlePaste,
              onDelete: handleDelete,
              onCellContextMenu: handleCellContextMenu,

              ...(hasActiveRegionView
                ? {}
                : {
                    onRowAppended: handleRowAppended,
                    trailingRowOptions: {
                      sticky: false,
                      tint: true,
                    },
                  }),
            }
          : sheetLayout.canEdit ||
              areCommentsEnabled ||
              canAskAiAboutCell ||
              canNoteFromSelection ||
              hasCellLinks ||
              canViewEditedCells
            ? { onCellContextMenu: handleCellContextMenu }
            : {})}
      />
    </div>
  );
};
